import { useEffect, useRef, useState } from 'react';
import { SquareStack } from 'lucide-react';
import WordHighlightMenu from './WordHighlightMenu';

/**
 * TeacherPassageActions — single entry point for teacher quick-actions on a
 * passage (spec §3.4). Wraps any reading-surface region and offers:
 *
 *   • Single-word selection → word add-to-course-list (via WordHighlightMenu)
 *   • Multi-word selection  → "Send sentence to Sentence Studio" floating pill
 *
 * The two action paths live under one wrapper so call sites only have to
 * mount one component to get both teacher affordances. Intended for the
 * teacher's live-session Watching · Context frame and any future surface
 * where a teacher needs to act on a student's reading passage.
 *
 * Props:
 *   chapterId         — BookChapter id, used as source for word adds and as
 *                       the `from_chapter_id` for sentence-studio enqueue.
 *   courseListOptions — array of { id, name, course_id, course_title } so
 *                       the WordHighlightMenu shows a 1-click add when there
 *                       is exactly one list. When omitted, the menu fetches
 *                       teacher's lists itself.
 *   children          — the passage region.
 *
 * Why "Teacher" in the name: this is explicitly a teacher-only affordance
 * layer. The student-facing version is just <WordHighlightMenu userRole=…>.
 */
export default function TeacherPassageActions({
  chapterId,
  courseListOptions = null,
  children,
}) {
  return (
    <WordHighlightMenu
      userRole="teacher"
      courseListOptions={courseListOptions}
      sourceType={chapterId ? 'BookChapter' : null}
      sourceId={chapterId || null}
    >
      <SentenceAnalyzePill chapterId={chapterId}>
        {children}
      </SentenceAnalyzePill>
    </WordHighlightMenu>
  );
}

/**
 * SentenceAnalyzePill — listens for multi-word selections inside the wrapped
 * region and shows a small floating pill that POSTs the selection to
 * /teacher/sentence_studio. Mirrors the chapter reader's "Analyze sentence"
 * action (ChapterReader.handleAnalyze) without the full SelectionToolbar.
 *
 * Co-exists with WordHighlightMenu because the two have non-overlapping
 * trigger conditions: this one no-ops on single-word selections, the menu
 * no-ops on multi-word selections.
 */
function SentenceAnalyzePill({ chapterId, children }) {
  const containerRef = useRef(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseUp = (e) => {
      if (e.target.closest('a, button, input, select, textarea, [role="button"], [data-vocabulary-menu], [data-sentence-analyze-pill]')) {
        return;
      }
      // Brief delay so the selection has settled.
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) return setPending(null);
        const raw = sel.toString();
        const trimmed = raw.trim();
        if (!trimmed) return setPending(null);
        // Single-word selections belong to WordHighlightMenu.
        if (trimmed.split(/\s+/).length < 2) return setPending(null);

        const range = sel.getRangeAt(0);
        if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
          return setPending(null);
        }
        const rect = range.getBoundingClientRect();
        setPending({
          text: trimmed,
          rect: { top: rect.top, left: rect.left, width: rect.width },
        });
      }, 50);
    };

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Dismiss on Escape, scroll, or outside click.
  useEffect(() => {
    if (!pending) return;
    const close = () => setPending(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onClick = (e) => {
      if (!e.target.closest('[data-sentence-analyze-pill]')) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('scroll', close, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('scroll', close);
    };
  }, [pending]);

  const submit = () => {
    if (!pending || !chapterId) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/teacher/sentence_studio';
    form.style.display = 'none';
    appendField(form, 'authenticity_token', document.querySelector('meta[name="csrf-token"]')?.content);
    appendField(form, 'from_chapter_id', String(chapterId));
    // Server resolves enclosing sentence from the raw text when offsets aren't given.
    appendField(form, 'text', pending.text);
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div ref={containerRef} className="relative">
      {children}
      {pending && (
        <div
          data-sentence-analyze-pill
          className="fixed z-50 pointer-events-auto"
          style={{
            left: `${pending.rect.left + pending.rect.width / 2}px`,
            top: `${pending.rect.top - 8}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <button
            type="button"
            onClick={submit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-lg hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
          >
            <SquareStack className="w-3.5 h-3.5" />
            Analyze sentence
          </button>
        </div>
      )}
    </div>
  );
}

function appendField(form, name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value ?? '';
  form.appendChild(input);
}
